import { X } from '@phosphor-icons/react/ssr';
import type { ComponentProps, ElementType, ReactNode } from 'react';

import { tv } from '../../lib/tv';

export type TagProps = Omit<ComponentProps<'span'>, 'children'> & {
  /** Figma "Size": Small 24 (default, Label/Small) · Medium 28 (Label/Medium). */
  size?: 'sm' | 'md';
  /** Figma "Leading icon" (Icon swap): a 12px icon before the label. */
  leadingIcon?: ElementType<{ className?: string }>;
  /**
   * Figma "Removable": shows the × button and is called when it is pressed.
   * Omit for a static tag.
   */
  onRemove?: () => void;
  /** Accessible name of the × button. Defaults to "Remove <label>". */
  removeLabel?: string;
  /** Figma State=Disabled: muted, and the × button is hidden. */
  disabled?: boolean;
  /** Figma "Label" - the value, e.g. "launch". */
  children: ReactNode;
};

/**
 * Figma Tag (92:59). bg/muted chip, radius/sm, gap space/1. Small: 24 tall,
 * 8px left / 4px right. Medium: 28 tall, 10 / 6. Hover: bg/emphasis with the
 * × button on bg/muted. Focus (keyboard on the × button): 2px border/focus
 * outside stroke = `ring-2`, no layout shift.
 */
export const tagStyles = tv({
  slots: {
    root: [
      'group/tag inline-flex w-fit shrink-0 items-center gap-1 rounded-sm whitespace-nowrap',
      'bg-bg-muted text-text-primary transition-[background-color,box-shadow] duration-[120ms] ease-out',
      'not-data-disabled:hover:bg-bg-emphasis',
      'has-[button:focus-visible]:ring-2 has-[button:focus-visible]:ring-border-focus',
      'data-disabled:bg-bg-disabled data-disabled:text-text-disabled',
    ],
    icon: 'text-icon-secondary size-3 shrink-0 group-data-disabled/tag:text-icon-tertiary',
    label: 'min-w-0 truncate',
    remove: [
      'text-icon-secondary flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-xs outline-none',
      'transition-colors duration-[120ms] ease-out group-hover/tag:bg-bg-muted',
    ],
    removeIcon: 'size-3',
  },
  variants: {
    size: {
      sm: { root: 'text-label-sm h-6 pr-1 pl-2' },
      md: { root: 'text-label-md h-7 pr-1.5 pl-2.5' },
    },
  },
  defaultVariants: { size: 'sm' },
});

/**
 * Removable token for values the user added - tags on a reel, recipients,
 * values in a Tag Input (Figma: Components ↳ Tag ↳ Tag). Use Badge for
 * read-only status. The × button is named "Remove <label>".
 */
export function Tag({
  size,
  leadingIcon: Icon,
  onRemove,
  removeLabel,
  disabled = false,
  className,
  children,
  ...props
}: TagProps) {
  const styles = tagStyles({ size });
  const name =
    removeLabel ?? (typeof children === 'string' ? `Remove ${children}` : 'Remove');
  return (
    <span
      data-slot="tag"
      data-disabled={disabled || undefined}
      className={styles.root({ className })}
      {...props}
    >
      {Icon && <Icon aria-hidden className={styles.icon()} />}
      {/* data-disabled on the text itself: the axe contrast exemption (see DESIGN-SYSTEM.md). */}
      <span data-disabled={disabled || undefined} className={styles.label()}>
        {children}
      </span>
      {onRemove && !disabled && (
        <button
          type="button"
          data-slot="tag-remove"
          aria-label={name}
          className={styles.remove()}
          onClick={onRemove}
        >
          <X aria-hidden className={styles.removeIcon()} />
        </button>
      )}
    </span>
  );
}
